import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { match } from 'ts-pattern';
import { isEmpty } from 'lodash';
import { MessageMedium } from 'constants/messageMediums';
import {
  FrequencyManagementSchema,
  MessageTypeSchema,
  MODE,
  CAPPING_MAX_ALLOWED,
  CAPPING_PERIOD,
  OPTIMIZE_PERIOD,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_MAX_ALLOWED,
} from '../contexts/FormProvider';
import { FrequencyMode, SupportedMediums } from 'modules/frequencyManagement/lib/types';

const createFieldsForMediumOff = (values: FrequencyManagementSchema) => ({
  mode: values[MODE],
  period: null,
  minAllowed: null,
  maxAllowed: null,
});

const createFieldsForMediumCap = (values: FrequencyManagementSchema) => ({
  mode: values[MODE],
  period: values[CAPPING_PERIOD],
  minAllowed: null,
  maxAllowed: values[CAPPING_MAX_ALLOWED],
});

const createFieldsForMediumOptimize = (values: FrequencyManagementSchema) => ({
  mode: values[MODE],
  period: values[OPTIMIZE_PERIOD],
  minAllowed: values[OPTIMIZE_MIN_ALLOWED],
  maxAllowed: values[OPTIMIZE_MAX_ALLOWED],
});

const CREATE_FIELDS_FOR_MEDIUM_BY_MODE = {
  [FrequencyMode.Optimize]: createFieldsForMediumOptimize,
  [FrequencyMode.Cap]: createFieldsForMediumCap,
  [FrequencyMode.Off]: createFieldsForMediumOff,
};

const createFieldsForMessageTypeOff = (values: MessageTypeSchema) => ({
  mode: values[MODE],
  period: null,
  maxAllowed: null,
  minAllowed: null,
});

const createFieldsForMessageTypeNoCap = (_values?: MessageTypeSchema) => ({
  /**
   * NoCap does not exist on the backend -- ignoreCap boolean is used instead.
   * So we set the mode to Off.
   */
  mode: FrequencyMode.Off,
  period: null,
  maxAllowed: null,
  minAllowed: null,
});

const createFieldsForMessageTypeCap = (values: MessageTypeSchema) => ({
  mode: values[MODE],
  period: values[CAPPING_PERIOD],
  maxAllowed: values[CAPPING_MAX_ALLOWED],
  minAllowed: null,
});

const createFieldsForMessageTypeOptimize = (values: MessageTypeSchema) => ({
  mode: values[MODE],
  period: values[OPTIMIZE_PERIOD],
  minAllowed: values[OPTIMIZE_MIN_ALLOWED],
  maxAllowed: values[OPTIMIZE_MAX_ALLOWED],
});

const CREATE_FIELDS__FOR_MESSAGE_TYPE_BY_MODE = {
  [FrequencyMode.Optimize]: createFieldsForMessageTypeOptimize,
  [FrequencyMode.Cap]: createFieldsForMessageTypeCap,
  [FrequencyMode.NoCap]: createFieldsForMessageTypeNoCap,
  [FrequencyMode.Off]: createFieldsForMessageTypeOff,
};

const createMessageTypeFieldsForMessageType = (values: MessageTypeSchema) => ({
  ignoreCap: values[MODE] === FrequencyMode.NoCap,
});

export const getUnsavedMediumModel = (
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<FrequencyManagementSchema>>>,
  messageMedium: SupportedMediums,
) => {
  const { messageTypes, ...mediumDirtyFields } = dirtyFields;
  if (isEmpty(mediumDirtyFields))
    return {
      isUnsaved: false,
      name: '',
    };
  return match(messageMedium)
    .with(MessageMedium.Email, () => ({
      isUnsaved: true,
      name: `${MessageMedium.Email} Frequency`,
    }))
    .with(MessageMedium.Push, () => ({
      isUnsaved: true,
      name: `${MessageMedium.Push} Frequency`,
    }))
    .with(MessageMedium.SMS, () => ({
      isUnsaved: true,
      name: `${MessageMedium.SMS} Frequency`,
    }))
    .otherwise(() => ({
      isUnsaved: true,
      name: `${MessageMedium.Email} Frequency`,
    }));
};

export const createUnsavedFormMessageTypeModel = (
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<FrequencyManagementSchema>>>,
  modalValues: FrequencyManagementSchema,
) => {
  /** Filter out any untouched Message Types */
  const dirtyMessageTypes = dirtyFields?.messageTypes || [];
  const current = {
    ...modalValues,
    /** dirtyFields tracks messageTypes in an array mapped by the "index" we add to messageType schema */
    messageTypes: modalValues.messageTypes.filter(
      (messageType) => dirtyMessageTypes[messageType.index as number],
    ),
  };
  return current;
};

export const createPayload = (
  messageMedium: SupportedMediums,
  current: FrequencyManagementSchema,
) => {
  const { mode, messageTypes } = current;
  const createFieldsForMedium =
    CREATE_FIELDS_FOR_MEDIUM_BY_MODE[mode as keyof typeof CREATE_FIELDS_FOR_MEDIUM_BY_MODE];

  return [
    {
      medium: messageMedium,
      fields: createFieldsForMedium(current),
    },
    // @ts-ignore
    ...messageTypes.map((messageType: MessageTypeSchema) => {
      const { id } = messageType;
      const createFieldsForMessageType = CREATE_FIELDS__FOR_MESSAGE_TYPE_BY_MODE[messageType[MODE]];
      return {
        medium: messageMedium,
        messageTypeId: id,
        fields: createFieldsForMessageType(messageType),
        messageTypeFields: createMessageTypeFieldsForMessageType(messageType),
      };
    }),
  ];
};
